import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { User } from '@/types/auth'
import { authApi } from '@/services/api'
import { socketService } from '@/services/socketService'
import { showSnackbar } from './uiSlice'
import { fetchAccessLimits } from './subscriptionSlice'

// Helper function to extract detailed error messages
const getErrorMessage = (error: any, defaultMessage: string): string => {
    const errorData = error.response?.data?.error
    if (errorData?.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
        // Show the first detailed error message, or join multiple if needed
        return errorData.details.length === 1
            ? errorData.details[0]
            : errorData.details.join(', ')
    }
    return errorData?.message || defaultMessage
}

type SubscriptionTier = 'free' | 'pro' | 'enterprise' | 'business'

interface AuthState {
    user: User | null
    token: string | null
    refreshToken: string | null
    isAuthenticated: boolean
    isLoading: boolean
    error: string | null
    isEmailVerified: boolean
    subscriptionTier: SubscriptionTier
    latestAccessLimits: User['monthlyAccessLimits'] | null
}

const initialState: AuthState = {
    user: null,
    token: localStorage.getItem('token'),
    refreshToken: localStorage.getItem('refreshToken'),
    isAuthenticated: !!localStorage.getItem('token'),
    isLoading: false,
    error: null,
    isEmailVerified: false,
    subscriptionTier: 'free',
    latestAccessLimits: null,
}

// Async thunks
export const loginUser = createAsyncThunk(
    'auth/login',
    async (credentials: { email: string; password: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.login(credentials)

            // Handle different response formats
            let authData = null;
            if (response.data?.user && response.data?.token) {
                // Direct format: { user, token, refreshToken }
                authData = response.data;
                // @ts-ignore - Handle nested response format: { success: true, data: { user, token, refreshToken } }
            } else if (response.data?.data?.user && response.data?.data?.token) {
                // Nested format: { success: true, data: { user, token, refreshToken } }
                // @ts-ignore - Accessing nested data property
                authData = response.data.data;
            }

            if (!authData || !authData.token || !authData.user) {
                const message = 'Invalid login response format'
                dispatch(showSnackbar({ message, type: 'error' }))
                return rejectWithValue(message)
            }

            localStorage.setItem('token', authData.token)
            localStorage.setItem('refreshToken', authData.refreshToken)
            dispatch(showSnackbar({ message: 'Login successful!', type: 'success' }))
            return authData
        } catch (error: any) {
            const errorData = error.response?.data?.error
            const message = getErrorMessage(error, 'Login failed')
            const errorType = errorData?.type

            // Handle account status errors with special messaging
            if (error.response?.status === 403) {
                let customMessage = message
                if (errorType === 'account_banned') {
                    customMessage = errorData?.reason
                        ? `Your account has been permanently banned. Reason: ${errorData.reason}`
                        : 'Your account has been permanently banned.'
                } else if (errorType === 'account_suspended') {
                    const endDate = errorData?.suspensionEndDate
                        ? new Date(errorData.suspensionEndDate).toLocaleDateString()
                        : 'further notice'
                    customMessage = errorData?.reason
                        ? `Your account is suspended until ${endDate}. Reason: ${errorData.reason}`
                        : `Your account is suspended until ${endDate}.`
                }
                dispatch(showSnackbar({
                    message: customMessage,
                    type: 'error',
                    duration: 10000
                }))
                return rejectWithValue({ message: customMessage, type: errorType, ...errorData })
            }

            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const registerUser = createAsyncThunk(
    'auth/register',
    async (userData: {
        email: string
        password: string
        role: 'worker' | 'employer'
        firstName: string
        lastName: string
        phone?: string
        city?: string
        coordinates?: [number, number]
    }, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.register(userData)
            // Don't store tokens during registration - user should login manually
            dispatch(showSnackbar({ message: 'Registration successful! Please check your email to verify your account.', type: 'success' }))
            return response.data
        } catch (error: any) {
            const message = getErrorMessage(error, 'Registration failed')
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const getCurrentUser = createAsyncThunk(
    'auth/getCurrentUser',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.getMe()

            // Handle different possible response structures
            let userData = null;
            if (response.data?.user) {
                // response.data.user
                userData = response.data.user;
                // @ts-ignore - Handle nested response: { success: true, data: { user: {...} } }
            } else if (response.data?.data?.user) {
                // response.data.data.user
                // @ts-ignore - Accessing nested data property
                userData = response.data.data.user;
                // @ts-ignore - Handle nested response: { success: true, data: { user data directly } }
            } else if (response.data?.data && typeof response.data.data === 'object' && (response.data.data._id || response.data.data.id)) {
                // response.data.data is the user object (for { success: true, data: { user data } })
                // @ts-ignore - Accessing nested data property
                userData = response.data.data;
                // @ts-ignore - Handle direct user data response
            } else if (response.data && typeof response.data === 'object' && (response.data._id || response.data.id)) {
                // response.data is the user object itself
                // @ts-ignore - Accessing user properties directly
                userData = response.data;
            }

            if (!userData) {
                console.error('Invalid response format - no user data found:', response.data)
                return rejectWithValue({ message: 'Invalid response format', status: 500 })
            }

            return userData
        } catch (error: any) {
            console.error('getCurrentUser error:', error)
            const errorData = error.response?.data?.error
            const message = errorData?.message || error.response?.data?.message || error.message || 'Failed to get user data'
            const status = error.response?.status || 500
            const errorType = errorData?.type

            // Handle account status errors with forced logout
            if (status === 403 && errorData?.forceLogout) {
                dispatch(showSnackbar({
                    message: message,
                    type: 'error',
                    duration: 8000
                }))
                // Clear tokens to force logout
                dispatch(clearTokens())
                return rejectWithValue({ message, status, type: errorType, forceLogout: true })
            }

            return rejectWithValue({ message, status, type: errorType })
        }
    }
)

export const updateUserDetails = createAsyncThunk(
    'auth/updateDetails',
    async (userData: Partial<User['profile']>, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.updateDetails(userData)
            dispatch(showSnackbar({ message: 'Profile updated successfully!', type: 'success' }))
            return response.data.user
        } catch (error: any) {
            const message = getErrorMessage(error, 'Update failed')
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const updatePassword = createAsyncThunk(
    'auth/updatePassword',
    async (passwords: { currentPassword: string; newPassword: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.updatePassword(passwords)
            dispatch(showSnackbar({ message: 'Password updated successfully. Please log in with your new password.', type: 'success' }))

            // Auto-logout after password update for security
            setTimeout(() => {
                dispatch(logoutUser())
            }, 3000)

            return response.data
        } catch (error: any) {
            const message = getErrorMessage(error, 'Password update failed')
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const forgotPassword = createAsyncThunk(
    'auth/forgotPassword',
    async (email: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.forgotPassword(email)
            dispatch(showSnackbar({ message: 'Password reset email sent!', type: 'success' }))
            return response.data.data
        } catch (error: any) {
            const message = getErrorMessage(error, 'Failed to send reset email')
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ token, password }: { token: string; password: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.resetPassword(token, password)
            localStorage.setItem('token', response.data.token)
            localStorage.setItem('refreshToken', response.data.refreshToken)
            dispatch(showSnackbar({ message: 'Password reset successful!', type: 'success' }))
            return response.data
        } catch (error: any) {
            const message = getErrorMessage(error, 'Password reset failed')
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const verifyEmail = createAsyncThunk(
    'auth/verifyEmail',
    async (payload: { email: string; code: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.verifyEmail(payload)
            dispatch(showSnackbar({ message: 'Email verified successfully!', type: 'success' }))
            return response.data
        } catch (error: any) {
            const message = getErrorMessage(error, 'Email verification failed')
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const resendVerification = createAsyncThunk(
    'auth/resendVerification',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.resendVerification()
            dispatch(showSnackbar({ message: 'Verification email sent! Check your inbox.', type: 'success' }))
            return response.data
        } catch (error: any) {
            const message = getErrorMessage(error, 'Failed to send verification email')
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const refreshToken = createAsyncThunk(
    'auth/refreshToken',
    async (_, { rejectWithValue }) => {
        try {
            const response = await authApi.refreshToken()

            const refreshData = response.data

            if (!refreshData?.token || !refreshData?.refreshToken) {
                throw new Error('Invalid refresh response format')
            }

            localStorage.setItem('token', refreshData.token)
            localStorage.setItem('refreshToken', refreshData.refreshToken)
            return refreshData
        } catch (error: any) {
            const message = getErrorMessage(error, 'Token refresh failed')
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            return rejectWithValue(message)
        }
    }
)

export const logoutUser = createAsyncThunk(
    'auth/logout',
    async (_, { dispatch: _dispatch }) => {
        try {
            await authApi.logout()
        } catch (error) {
            // Continue with logout even if API call fails
        } finally {
            // Disconnect socket before clearing tokens
            socketService.disconnect()
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            _dispatch(showSnackbar({ message: 'Logged out successfully', type: 'info' }))
        }
    }
)

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null
        },
        setTokens: (state, action: PayloadAction<{ token: string; refreshToken: string }>) => {
            state.token = action.payload.token
            state.refreshToken = action.payload.refreshToken
            localStorage.setItem('token', action.payload.token)
            localStorage.setItem('refreshToken', action.payload.refreshToken)
        },
        clearTokens: (state) => {
            state.token = null
            state.refreshToken = null
            state.user = null
            state.isAuthenticated = false
            state.subscriptionTier = 'free'
            state.latestAccessLimits = null
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            // Disconnect socket when clearing tokens
            socketService.disconnect()
        },
        updateUser: (state, action: PayloadAction<User>) => {
            state.user = action.payload
            state.subscriptionTier = action.payload.subscription?.tier as SubscriptionTier || state.subscriptionTier
            state.latestAccessLimits = action.payload.monthlyAccessLimits || state.latestAccessLimits
        },
        updateUserAvatar: (state, action: PayloadAction<string>) => {
            if (state.user) {
                state.user.profile.avatar = action.payload
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAccessLimits.fulfilled, (state, action) => {
                const previousLimits = state.latestAccessLimits || state.user?.monthlyAccessLimits || null
                const mappedLimits = {
                    gigs: {
                        current: action.payload.accessLimits.gigs.current,
                        limit: action.payload.accessLimits.gigs.limit,
                        resetAt: action.payload.accessLimits.gigs.resetAt || previousLimits?.gigs.resetAt || '',
                    },
                    applications: {
                        current: action.payload.accessLimits.applications.current,
                        limit: action.payload.accessLimits.applications.limit,
                        resetAt: action.payload.accessLimits.applications.resetAt || previousLimits?.applications.resetAt || '',
                    },
                    featuredListings: previousLimits?.featuredListings,
                }

                state.subscriptionTier = action.payload.accessLimits.subscriptionTier as SubscriptionTier || state.subscriptionTier
                state.latestAccessLimits = mappedLimits

                if (state.user) {
                    const existingSubscription = state.user.subscription
                    state.user = {
                        ...state.user,
                        subscription: existingSubscription
                            ? { ...existingSubscription, tier: action.payload.accessLimits.subscriptionTier as SubscriptionTier }
                            : existingSubscription,
                        monthlyAccessLimits: mappedLimits,
                    }
                }
            })

        // Login
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false
                state.user = action.payload?.user || null
                state.token = action.payload?.token || null
                state.refreshToken = action.payload?.refreshToken || null
                state.isAuthenticated = !!action.payload?.user
                state.isEmailVerified = action.payload?.user?.emailVerified || false
                state.subscriptionTier = action.payload?.user?.subscription?.tier as SubscriptionTier || 'free'
                state.latestAccessLimits = action.payload?.user?.monthlyAccessLimits || null

                // Store tokens in localStorage for persistence
                if (action.payload?.token && action.payload?.refreshToken) {
                    localStorage.setItem('token', action.payload.token)
                    localStorage.setItem('refreshToken', action.payload.refreshToken)
                }
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
                state.isAuthenticated = false
            })

        // Register
        builder
            .addCase(registerUser.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(registerUser.fulfilled, (state) => {
                state.isLoading = false
                // Don't set authentication state during registration - user should login manually
                state.error = null
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
                state.isAuthenticated = false
            })

        // Get Current User
        builder
            .addCase(getCurrentUser.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.isLoading = false
                state.user = action.payload
                state.isAuthenticated = true
                state.isEmailVerified = action.payload?.emailVerified || false
                state.subscriptionTier = action.payload?.subscription?.tier as SubscriptionTier || 'free'
                state.latestAccessLimits = action.payload?.monthlyAccessLimits || null
            })
            .addCase(getCurrentUser.rejected, (state, action) => {
                state.isLoading = false
                const error = action.payload as any
                state.error = (typeof error === 'object' && error?.message) ? error.message : (error || 'Failed to get user data')
                state.isAuthenticated = false
                state.user = null

                // Only clear tokens if it's an authentication error (401/403)
                if (error?.status === 401 || error?.status === 403) {
                    state.token = null
                    state.refreshToken = null
                    localStorage.removeItem('token')
                    localStorage.removeItem('refreshToken')
                }
            })

        // Update Details
        builder
            .addCase(updateUserDetails.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(updateUserDetails.fulfilled, (state, action) => {
                state.isLoading = false
                state.user = action.payload
                state.subscriptionTier = action.payload.subscription?.tier as SubscriptionTier || state.subscriptionTier
                state.latestAccessLimits = action.payload.monthlyAccessLimits || state.latestAccessLimits
            })
            .addCase(updateUserDetails.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Update Password
        builder
            .addCase(updatePassword.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(updatePassword.fulfilled, (state, action) => {
                state.isLoading = false
                state.token = action.payload?.token || state.token
                state.refreshToken = action.payload?.refreshToken || state.refreshToken

                // Update localStorage if new tokens are provided
                if (action.payload?.token) {
                    localStorage.setItem('token', action.payload.token)
                }
                if (action.payload?.refreshToken) {
                    localStorage.setItem('refreshToken', action.payload.refreshToken)
                }
            })
            .addCase(updatePassword.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Forgot Password
        builder
            .addCase(forgotPassword.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(forgotPassword.fulfilled, (state) => {
                state.isLoading = false
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Reset Password
        builder
            .addCase(resetPassword.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.isLoading = false
                state.user = action.payload.user
                state.token = action.payload.token
                state.refreshToken = action.payload.refreshToken
                state.isAuthenticated = true
                state.isEmailVerified = action.payload.user.emailVerified
                state.subscriptionTier = action.payload.user.subscription?.tier as SubscriptionTier || state.subscriptionTier
                state.latestAccessLimits = action.payload.user.monthlyAccessLimits || state.latestAccessLimits
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Email Verification
        builder
            .addCase(verifyEmail.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(verifyEmail.fulfilled, (state) => {
                state.isLoading = false
                state.isEmailVerified = true
                if (state.user) {
                    state.user.emailVerified = true
                    state.user.profile.verified = true
                    state.subscriptionTier = state.user.subscription?.tier as SubscriptionTier || state.subscriptionTier
                    state.latestAccessLimits = state.user.monthlyAccessLimits || state.latestAccessLimits
                }
            })
            .addCase(verifyEmail.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Resend Verification
        builder
            .addCase(resendVerification.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(resendVerification.fulfilled, (state) => {
                state.isLoading = false
            })
            .addCase(resendVerification.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Refresh Token
        builder
            .addCase(refreshToken.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.isLoading = false
                state.token = action.payload.token
                state.refreshToken = action.payload.refreshToken
                state.user = action.payload.user
                state.isAuthenticated = true
                state.isEmailVerified = action.payload.user.emailVerified
                state.subscriptionTier = action.payload.user.subscription?.tier as SubscriptionTier || state.subscriptionTier
                state.latestAccessLimits = action.payload.user.monthlyAccessLimits || state.latestAccessLimits
            })
            .addCase(refreshToken.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
                state.isAuthenticated = false
                state.user = null
                state.token = null
                state.refreshToken = null
            })

        // Logout
        builder
            .addCase(logoutUser.fulfilled, (state) => {
                state.user = null
                state.token = null
                state.refreshToken = null
                state.isAuthenticated = false
                state.isLoading = false
                state.error = null
                state.isEmailVerified = false
                state.subscriptionTier = 'free'
                state.latestAccessLimits = null
            })
    },
})

export const { clearError, setTokens, clearTokens, updateUser, updateUserAvatar } = authSlice.actions
export default authSlice.reducer